<template>
	<core-layout-content full>
		<!-- Headline -->
		<core-layout-headline-and-actions>
			<template slot="headline">{{ $t("reports") }}</template>
		</core-layout-headline-and-actions>

		<div class="flex xs-12 xs-left xs-middle bb1 bc7">
			<!-- Select Report Type dropdown -->
			<div class="xs-12 md-12">
				<core-form-input
					componentName="j-base-select"
					name="selectReportType"
					:options="options"
					v-validate="'required'"
					v-model="selectedType"
				/>
			</div>
		</div>

		<!-- Filter -->
		<div class="xs-12">
			<asia-report-filter
				:isExpanded="!isLoading"
				:for="selectedType"
				:submitButtonText="$t('download') + '.xlsx'"
				:forceReset="forceFilterReset"
				@submit="handleExport"
			/>
		</div>

		<!-- Loading -->
		<div
			class="flex wrap xs-12 xs-between mt2 mb2 bgWarning p4 pl2 pr2 c1 r2 fw7 fs3"
			v-if="isLoading"
		>
			{{ $t("preparing_your_report") }}
			<j-spinner size="small" />
		</div>
	</core-layout-content>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JSpinner from "@/components/JSpinner"
import api from "../../api/"
import { mapState } from "vuex"
import XLSX from "xlsx"
import { convertToOctetStream } from "@/utils/"
import saveAs from "file-saver"
import individualsSchema from "../../schemas/individualsSchema.js"
import companiesSchema from "../../schemas/companiesSchema.js"
import policiesSchema from "../../schemas/policiesSchema.js"
import quotesSchema from "../../schemas/quotesSchema.js"
import commissionsSchema from "../../schemas/commissionsSchema.js"
import commissionsModifier from "../../modifiers/commissionsModifier.js"
import quotesNotesSchema from "../../schemas/quotesNotesSchema.js"
import policiesNotesSchema from "../../schemas/policiesNotesSchema.js"
import dayjs from "dayjs"
import AsiaReportFilter from "./AsiaReportFilter/"
import VhisSchemas from "./schemas/"
import AegSchemas from "../../schemas/aegSchema/index.js"
import get from "lodash.get"

export default {
	components: {
		CoreLayoutContent,
		CoreFormInput,
		CoreLayoutHeadlineAndActions,
		JSpinner,
		AsiaReportFilter
	},
	data() {
		return {
			isLoading: false,
			selectedType: "policies",
			vhisTemplate: null,
			forceFilterReset: 0
		}
	},
	computed: {
		...mapState(["settings"]),

		settings() {
			return this.$store.state.settings
		},

		options() {
			return [
				{
					name: this.$t("policies"),
					value: "policies"
				},
				{
					name: this.$t("quotes"),
					value: "quotes"
				},
				{
					name: this.$t("clients_individuals"),
					value: "individuals"
				},
				{
					name: this.$t("clients_companies"),
					value: "companies"
				},
				{
					name: this.$t("commissions"),
					value: "commissions"
				},
				{
					name: this.$t("quotes_notes"),
					value: "quotes_notes"
				},
				{
					name: this.$t("policies_notes"),
					value: "policies_notes"
				},
				{
					name:
						"1. Number of applications by five underwriting/re-underwriting, by age and sex of Insured Person",
					value: "template_01"
				},
				{
					name:
						"2. Number of applications by five underwriting/re-underwriting, by place of Residence of Insured Person",
					value: "template_02"
				},
				{
					name:
						"3. Number of applications by five Underwriting / Re-underwriting results, by whether the Insured Person is newly insured or insured through Migration",
					value: "template_03"
				},
				{
					name:
						"4. Record of applications with Premium Loading/CBE/declined status ",
					value: "template_04"
				},
				{
					name:
						"5. Total number of policies, Policy Holders and Insured Person",
					value: "template_05"
				},
				{
					name:
						"6. Number of policies by four underwriting/re-underwriting results, by age and sex of Insured Person",
					value: "template_06"
				},
				{
					name:
						"7. Number of policies by four underwriting/re-underwriting results, by place of Residence of Insured Person",
					value: "template_07"
				},
				{
					name:
						"8. Number of policies by four Underwriting / Re-underwriting results, by whether the Insured Person is newly insured or insured through Migration",
					value: "template_08"
				},
				{
					name: "9. Record of policies with Premium Loading/CBE",
					value: "template_09"
				},
				{
					name:
						"10. Number of policies by whether one of the Policy Holder(s) is the Insured Person",
					value: "template_10"
				},
				{
					name:
						"11. Claim counts by approved claim and rejected claim with reasons",
					value: "template_11"
				},
				{
					name: "12. Claim records",
					value: "template_12"
				},
				{
					name: "13. Premium and expense statistics",
					value: "template_13"
				},
				{
					name:
						"A1. Number of applications by five underwriting/re-underwriting, by age and sex of Insured Person",
					value: "template_a1"
				},
				{
					name:
						"A2. Number of accepted/rejected offer of customers by four Underwriting/ Re-underwriting results, by Place of Residence of Insured Person",
					value: "template_a2"
				},
				{
					name:
						"A3. Number of accepted/rejected offer of customers by four Underwriting/ Re-underwriting results, by whether the Inusred Person is newly insured through Migration",
					value: "template_a3"
				},
				{
					name:
						"A4. Number of policies expired, by four Underwriting/ Re-underwriting results, by age and sex of Insured Person",
					value: "template_a4"
				},
				{
					name:
						"A5. Number of policies expired, by four Underwriting/ Re-underwriting results, by Place of Residence of Insured Person",
					value: "template_a5"
				},
				{
					name:
						"A6. Number of policies expired, by four Underwriting/ Re-underwriting results, by whether the Insured Person is newly insured or isured through Migration",
					value: "template_a6"
				},
				{
					name:
						"A7. Number of policies effective, by four Underwriting/ Re-underwriting results, by age and sex of Insured Person",
					value: "template_a7"
				},
				{
					name:
						"A8. Number of policies effective, by four Underwriting/ Re-underwriting results, by Place of Residence of Insured Person",
					value: "template_a8"
				},
				{
					name:
						"A9. Number of policies effected, by four Underwriting/ Re-underwriting results, by whether the Insured Person is newly insured or isured through Migration",
					value: "template_a9"
				}
			]
		}
	},

	methods: {
		async fetchTemplates() {
			const tenantId = this.$store.state.tenantId
			const res = await api.fetchTemplates({ tenantId })

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			// Store schema in reports store
			this.$store.commit("reports/SET_TEMPLATES", res.data)
		},

		async handleExport({ payload, where }) {
			console.log("payload", payload, "where:", where)
			// Show error helper function
			const showError = () => {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
			}

			// If some report is already in progress, prevent
			if (this.isLoading) return

			// Start loading
			this.isLoading = true

			//-----------------------------------------------------
			// Define different apiKeys depending on tab selected
			// ----------------------------------------------------
			// Default is for policies
			let apiKeys = {
				getCount: "numberOfPolicies",
				getData: "readAllPolicies",
				response: "policies"
			}
			let sort = { fieldName: "createdAt", type: "desc" }
			let finalWhere = where

			if (this.selectedType === "policies") {
				let sort = { fieldName: "issueDate", type: "desc" }
			}

			if (
				this.selectedType === "quotes" ||
				this.selectedType === "quotes_notes"
			) {
				apiKeys = {
					getCount: "numberOfPolicies",
					getData: "readAllQuotes",
					response: "policies"
				}
				finalWhere = where
			}

			if (this.selectedType === "individuals") {
				apiKeys = {
					getCount: "numberOfIndividuals",
					getData: "readAllIndividuals",
					response: "individuals"
				}
				finalWhere = { and: [{ type_in: ["CUSTOMER"] }, where] }
			}

			if (this.selectedType === "companies") {
				apiKeys = {
					getCount: "numberOfCompanies",
					getData: "readAllCompanies",
					response: "companies"
				}
				finalWhere = { and: [{ type_in: ["CUSTOMER"] }, where] }
			}

			if (this.selectedType === "commissions") {
				apiKeys = {
					getCount: "numberOfPolicies",
					getData: "readAllPolicies",
					response: "policies"
				}
				finalWhere = where
			}

			if (this.selectedType.includes("template")) {
				apiKeys = {
					getCount: null,
					getData: null,
					response: null
				}
				finalWhere = where
			}

			// Prepare fetchedData
			let fetchedData = []

			if (apiKeys?.getData) {
				//-----------------------------------------------------
				// Fetch number of items
				// ----------------------------------------------------
				let resNumberOfItems = await api[apiKeys.getCount]({
					where: finalWhere
				})
				if (resNumberOfItems.errors) {
					showError()
					return
				}

				let numberOfItems = resNumberOfItems.data[apiKeys.response].totalCount

				//-----------------------------------------------------
				//  Batch download of data from server
				// ----------------------------------------------------
				const limit = 100
				const numberOfBatches = Math.ceil(numberOfItems / limit).toFixed(0)

				// fetch data in batches, waiting for each call to finish before making another one
				for (let i = 0; i < numberOfBatches; i++) {
					const res = await api[apiKeys.getData]({
						limit,
						sort,
						skip: i * limit,
						where: finalWhere,
						tenantId: this.$store.state.tenantId
					})

					if (res.errors) {
						showError()
						return
					}

					// assign res to fetchedData
					fetchedData = [...fetchedData, ...res.data[apiKeys.response].list]
				}
			}

			this.isLoading = false

			// Run some modifiers on data if necessary
			// E.g. commissions will flatten data
			if (this.selectedType === "commissions")
				fetchedData = commissionsModifier(fetchedData)

			//-----------------------------------------------------
			//  Generate excel
			// ----------------------------------------------------
			// For xls generation we use sheetJS
			// https://docs.sheetjs.com/#streaming-write

			// Create a new workbook and set some props
			let workBook = XLSX.utils.book_new()
			const today = dayjs().format("DD-MM-YYYY_HH-mm-ss")
			const fileName =
				this.selectedType === "vhis"
					? `${this.selectedType}-${this.vhisTemplate}-${today}`
					: `${this.selectedType}-${today}`

			workBook.Props = {
				Title: fileName,
				Subject: `Report - ${this.selectedType}`,
				Author: "CoverGo",
				CreatedDate: new Date()
			}

			// Push a new sheet to the workbook
			workBook.SheetNames.push(this.selectedType)

			// Prepare data for policies
			// Init schema based on user select
			let schema = null
			if (this.selectedType === "policies")
				schema = policiesSchema(this, fetchedData)
			if (this.selectedType === "quotes")
				schema = quotesSchema(this, fetchedData)
			if (this.selectedType === "individuals")
				schema = individualsSchema(this, fetchedData)
			if (this.selectedType === "companies")
				schema = companiesSchema(this, fetchedData)
			if (this.selectedType === "commissions")
				schema = commissionsSchema(this, fetchedData)
			if (this.selectedType === "quotes_notes")
				schema = quotesNotesSchema(this, fetchedData)
			if (this.selectedType === "policies_notes")
				schema = policiesNotesSchema(this, fetchedData)

			// for vhis
			if (this.selectedType?.includes("template")) {
				this.isLoading = true
				schema = await VhisSchemas[this.selectedType](where)
				this.isLoading = false
			}

			//==================================================================
			// for aeg, regex check if selectedType first three characters are `aeg`//`abc`
			// Fetched data happening inside js file to fetch specific data for each report
			//==================================================================
			if (this.selectedType.match(/[abc]{3}/)?.length > 0)
				schema = await AegSchemas[this.selectedType](payload)

			// Creates workSheet from data in schema
			// Schema returns object { workSheetData, workSheetColumns, workSheetMerges }
			const workSheet = XLSX.utils.aoa_to_sheet(schema.workSheetData)

			// Apply Cell Column width settings
			workSheet["!cols"] = schema.workSheetColumns

			// Apply Cell Merges
			if (!workSheet["!merges"]) workSheet["!merges"] = []
			const merges = schema.workSheetMerges ? schema.workSheetMerges : []
			workSheet["!merges"] = [...merges]

			workBook.Sheets[this.selectedType] = workSheet

			const xlsBinary = XLSX.write(workBook, {
				bookType: "xlsx",
				type: "binary"
			})

			// Download
			// Prevent file download in cypress test
			if (!window.Cypress) {
				saveAs(
					new Blob([convertToOctetStream(xlsBinary)], {
						type: "application/octet-stream"
					}),
					`${fileName}.xlsx`
				)
			}
		}
	},

	created() {
		this.fetchTemplates()
	}
}
</script>
